import type { ConsultationResponseDto, WaitlistAvailability } from '@montugroup/pms-api-contracts';
import { useState } from 'react';

import type { WaitlistFormMode } from '@/pages/consultations/hooks/useSubmitWaitlistPreference';
import { useSubmitWaitlistPreference } from '@/pages/consultations/hooks/useSubmitWaitlistPreference';

interface UseWaitlistPreferenceFormOptions {
  consultation: Pick<ConsultationResponseDto, 'id' | 'code'>;
  mode: WaitlistFormMode;
  /** announced once the join or change is saved */
  successMessage: string;
  onOutcome: (message: string) => void;
  /** called when pms-service refuses; the section explains it, as the refetch may unmount the caller */
  onRefused: () => void;
  /** called when the patient opens the form, which replaces an earlier refusal */
  onAction: () => void;
}

/**
 * Opens and submits the waitlist preference form on the consultation card, and returns focus to the button that
 * opened it after a cancel.
 * @param options - the consultation, the form's mode, and what to call for each outcome
 * @returns whether the form is open, a handler that opens it, and props for the form and its opening button
 */
export function useWaitlistPreferenceForm(options: UseWaitlistPreferenceFormOptions) {
  const { consultation, mode, successMessage, onOutcome, onRefused, onAction } = options;
  const [isFormOpen, setIsFormOpen] = useState(false);
  // the button unmounts while the form is open, so it's focused when it comes back after a cancel
  const [shouldFocusButton, setShouldFocusButton] = useState(false);
  const { submit, isPending, errorMessage, clearError } = useSubmitWaitlistPreference();

  const open = () => {
    clearError();
    onAction();
    setIsFormOpen(true);
  };

  const handleSubmit = async (availability: WaitlistAvailability) => {
    const outcome = await submit(consultation, availability, mode);
    if (outcome === 'failed') {
      return;
    }
    setIsFormOpen(false);
    if (outcome === 'notEligible') {
      onRefused();
      return;
    }
    onOutcome(successMessage);
  };

  return {
    isFormOpen,
    open,
    formProps: {
      isSubmitting: isPending,
      errorMessage,
      onSubmit: handleSubmit,
      onCancel: () => {
        setIsFormOpen(false);
        setShouldFocusButton(true);
      },
    },
    buttonFocusProps: { autoFocus: shouldFocusButton, onFocus: () => setShouldFocusButton(false) },
  };
}
